import classNames from 'classnames';
import { memo, useCallback } from 'react';

import { LineweightMediumIcon, MessageCircleIcon, VectorSquareIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';

import { type AnnotationTool, useAnnotationV2Store } from '~/components/AnnotationsV2/providers/AnnotationV2Provider';
import { ANNOTATION_TOOL_PENCIL_BUTTON } from '~/constants/testIDs';

export const ANNOTATION_TOOL_POINT_BUTTON = 'ANNOTATION_TOOL_POINT_BUTTON';
export const ANNOTATION_TOOL_BOX_BUTTON = 'ANNOTATION_TOOL_BOX_BUTTON';

const toolIcons = {
  point: MessageCircleIcon,
  box: VectorSquareIcon,
  pencil: LineweightMediumIcon,
} as const;

const toolTestIds = {
  point: ANNOTATION_TOOL_POINT_BUTTON,
  box: ANNOTATION_TOOL_BOX_BUTTON,
  pencil: ANNOTATION_TOOL_PENCIL_BUTTON,
} as const;

export const AnnotationToolSelector = memo(() => {
  const selectedTool = useAnnotationV2Store((state) => state.selectedTool);
  const setSelectedTool = useAnnotationV2Store((state) => state.setSelectedTool);

  const handleToolSelect = useCallback(
    (tool: AnnotationTool) => {
      setSelectedTool(tool);
    },
    [setSelectedTool],
  );

  return (
    <div className="flex items-center gap-1">
      {(['point', 'box', 'pencil'] as const).map((tool) => {
        const IconComponent = toolIcons[tool];
        const isSelected = selectedTool === tool;

        return (
          <IconButton
            key={tool}
            adsIcon
            appearance="ghost"
            className={classNames(
              'p-1',
              isSelected
                ? 'ads:bg-ads-ember-9 bg-orange-7 ads:text-inverse text-white ads:hover:bg-ads-ember-9 hover:bg-orange-7 ads:hover:text-inverse hover:text-white'
                : 'ads:text-icon-default text-gray-11 ads:hover:text-icon-emphasis hover:text-gray-12',
            )}
            color="gray"
            data-testid={toolTestIds[tool]}
            icon={IconComponent}
            label={`${tool.charAt(0).toUpperCase() + tool.slice(1)} annotation`}
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            onClick={() => handleToolSelect(tool)}
            size="small"
          />
        );
      })}
    </div>
  );
});

AnnotationToolSelector.displayName = 'AnnotationToolSelector';
